import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Card } from 'react-bootstrap';
import { reprocessAudio, queryLogs } from '../services/api';

const ReprocessModal = ({ show, onHide, video, onReprocessSuccess }) => {
  const [selectedNarrator, setSelectedNarrator] = useState('Won');
  const [startTimeInput, setStartTimeInput] = useState(() => {
    // Initialize with current UTC time (datetime-local format: YYYY-MM-DDTHH:mm)
    return new Date().toISOString().slice(0, 16);
  });
  const [secondsInput, setSecondsInput] = useState('00');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [overlapData, setOverlapData] = useState(null);

  useEffect(() => {
    if (!show || !video?.id) return;

    let isMounted = true;
    const fetchLastStartRecord = async () => {
      try {
        const res = await queryLogs({
          video_id: video.id,
          action: 'start_record',
          narrator: selectedNarrator,
          limit: 1,
        });

        if (isMounted && res?.data?.items?.length > 0) {
          const lastLog = res.data.items[0];
          if (lastLog?.timestamp) {
            const dt = new Date(lastLog.timestamp);
            if (!isNaN(dt.getTime())) {
              setStartTimeInput(dt.toISOString().slice(0, 16));
              setSecondsInput(String(dt.getUTCSeconds()).padStart(2, '0'));
              return;
            }
          }
        }
      } catch (err) {
        console.warn('Could not fetch last start_record log:', err);
      }

      if (isMounted) {
        setStartTimeInput(new Date().toISOString().slice(0, 16));
        setSecondsInput('00');
      }
    };

    fetchLastStartRecord();

    return () => {
      isMounted = false;
    };
  }, [show, video?.id, selectedNarrator]);

  if (!video || !video.audio_description) {
    return null;
  }

  const hasWon = video.audio_description.s3_link && video.audio_description.s3_link !== '';
  const hasWaan = video.audio_description.s3_link_waan && video.audio_description.s3_link_waan !== '';

  // Convert datetime-local input + seconds to ISO string (UTC)
  const datetimeToISO = (datetimeLocalString, seconds) => {
    if (!datetimeLocalString) return '';
    const paddedSeconds = String(seconds).padStart(2, '0');
    return datetimeLocalString + ':' + paddedSeconds + 'Z';
  };

  const handleReprocess = async (sessionAction = 'auto') => {
    setError(null);
    setSuccessMessage(null);
    setOverlapData(null);

    try {
      if (!selectedNarrator) {
        setError('Please select a narrator');
        return;
      }

      const startTimeISO = datetimeToISO(startTimeInput, secondsInput);
      if (!startTimeISO) {
        setError('Please select a start time');
        return;
      }

      setIsProcessing(true);
      
      const response = await reprocessAudio(video.id, selectedNarrator, startTimeISO, sessionAction);

      if (response.data.success) {
        setSuccessMessage(`Audio reprocessed successfully for ${selectedNarrator}!`);
        setTimeout(() => {
          handleClose();
          onReprocessSuccess(response.data.audio_description);
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to reprocess audio');
      }
    } catch (err) {
      console.error('Error reprocessing audio:', err);
      if (err.response?.status === 409 && err.response?.data?.detail?.overlap) {
        setOverlapData(err.response.data.detail);
      } else {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : (detail?.message || err.message);
        setError(`Failed to reprocess audio: ${msg}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setError(null);
      setSuccessMessage(null);
      setOverlapData(null);
      setStartTimeInput(new Date().toISOString().slice(0, 16));
      setSecondsInput('00');
      setSelectedNarrator('Won');
      onHide();
    }
  };

  const formatDuration = (seconds) => {
    if (seconds == null) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s (${Math.round(seconds)}s)`;
  };

  // Determine which narrators are available
  const availableNarrators = [];
  if (hasWon) availableNarrators.push('Won');
  if (hasWaan) availableNarrators.push('Waan');

  return (
    <Modal show={show} onHide={handleClose} centered backdrop={isProcessing ? 'static' : true} size={overlapData ? 'lg' : 'md'}>
      <Modal.Header closeButton={!isProcessing}>
        <Modal.Title>🔄 Reprocess Audio</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {successMessage && <Alert variant="success">{successMessage}</Alert>}

        {overlapData ? (
          <div>
            <Alert variant="warning">
              <Alert.Heading className="h6 mb-2">⚠️ Session Overlap Detected</Alert.Heading>
              <p className="mb-2">
                The session to be created for this audio (<strong>{overlapData.new_session?.start}</strong> to <strong>{overlapData.new_session?.stop}</strong>, Duration: {formatDuration(overlapData.new_session?.duration)}) overlaps with <strong>{overlapData.overlapping_sessions?.length}</strong> existing recording session(s):
              </p>
              <Card className="bg-light mb-3">
                <Card.Body className="py-2 px-3">
                  <ul className="mb-0 ps-3">
                    {overlapData.overlapping_sessions?.map((s, idx) => (
                      <li key={s.start_event_id || idx} className="small">
                        <strong>Session #{s.start_event_id}</strong>: {s.start} — {s.stop || 'ongoing'} {s.duration != null && `(${formatDuration(s.duration)})`}
                      </li>
                    ))}
                  </ul>
                </Card.Body>
              </Card>
              <p className="mb-0">
                Please select how you would like to proceed:
              </p>
            </Alert>

            <div className="d-flex flex-column gap-2 mt-3">
              <Button
                variant="danger"
                onClick={() => handleReprocess('overwrite')}
                disabled={isProcessing}
                className="text-start py-2"
              >
                <strong>🔄 Overwrite Session</strong>
                <div className="small text-white-50">
                  Delete overlapping session(s) and create a new session spanning the full reprocessed audio for reenactment.
                </div>
              </Button>

              <Button
                variant="outline-primary"
                onClick={() => handleReprocess('skip')}
                disabled={isProcessing}
                className="text-start py-2"
              >
                <strong>⚡ Reprocess Without Session</strong>
                <div className="small text-muted">
                  Overlay sound effects and save the processed audio without creating or modifying any sessions.
                </div>
              </Button>

              <Button
                variant="secondary"
                onClick={() => setOverlapData(null)}
                disabled={isProcessing}
                className="text-start py-2"
              >
                <strong>↩️ Back / Edit Start Time</strong>
                <div className="small text-white-50">
                  Adjust the start time and try again.
                </div>
              </Button>
            </div>
          </div>
        ) : availableNarrators.length === 0 ? (
          <Alert variant="warning">
            No raw audio found for reprocessing. Please upload audio first.
          </Alert>
        ) : (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select Narrator</Form.Label>
              <Form.Select
                value={selectedNarrator}
                onChange={(e) => setSelectedNarrator(e.target.value)}
                disabled={isProcessing}
              >
                {availableNarrators.map((narrator) => (
                  <option key={narrator} value={narrator}>
                    {narrator}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Available: {availableNarrators.join(', ')}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Start Time (UTC)</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="datetime-local"
                  value={startTimeInput}
                  onChange={(e) => setStartTimeInput(e.target.value)}
                  disabled={isProcessing}
                  style={{ flex: 1 }}
                />
                <div className="d-flex align-items-end gap-2">
                  <Form.Group className="mb-0">
                    <Form.Label className="mb-2" style={{ fontSize: '0.875rem' }}>
                      Seconds
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="59"
                      value={secondsInput}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        const clamped = Math.max(0, Math.min(59, val));
                        setSecondsInput(String(clamped).padStart(2, '0'));
                      }}
                      disabled={isProcessing}
                      style={{ width: '60px' }}
                    />
                  </Form.Group>
                </div>
              </div>
              <Form.Text className="text-muted">
                Select the UTC datetime from which the recording started.
                A reenactment session will automatically be created for this window.
              </Form.Text>
            </Form.Group>

            <div className="alert alert-info">
              <strong>Note:</strong> This will re-process the raw audio file with sound effect overlays
              from the specified start time, save it to the processed file, and automatically configure a reenactment session.
            </div>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        {!overlapData && availableNarrators.length > 0 && (
          <Button
            variant="primary"
            onClick={() => handleReprocess('auto')}
            disabled={isProcessing || availableNarrators.length === 0}
          >
            {isProcessing ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              '🔄 Reprocess'
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ReprocessModal;

